import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Container from "react-bootstrap/Container";
import SiteHead from "./SiteHead";
import log from "loglevel";
import { STATIC_FILES_ENDPOINT } from "api-config";
import hljs from "highlight.js/lib/core";
import plaintext from "highlight.js/lib/languages/plaintext";
import python from "highlight.js/lib/languages/python";
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";

import "highlight.js/styles/atom-one-dark.css";

import "sass/create-blog.scss";
import "sass/blog.scss";
import "sass/ckeditor.css";

// Only register the languages we need in order to reduce JS Bundle Size.
hljs.registerLanguage("plaintext", plaintext);
hljs.registerLanguage("python", python);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("typescript", typescript);

const getBlogImagePath = (fileName) => {
  return `${STATIC_FILES_ENDPOINT}/bf/${fileName}`;
};

export const BlogPostContent = (props) => {
  log.debug("BlogPost::render()", props);
  const {
    pageContext: { post },
  } = props;
  const {
    title,
    meta_title,
    author,
    meta_description,
    content,
    image_name,
    alt_tag,
    created_on,
  } = post;

  useEffect(() => {
    // Inspired by: https://github.com/crpietschmann/hljslanguagedisplayplugin/blob/main/dist/js/hljslanguagedisplayplugin.js
    hljs.addPlugin({
      "after:highlightElement": ({ el, text }) => {
        // Extract the language name from the class list of the <code> tag
        let languageClass = Array.from(el.classList).find((cls) =>
          cls.startsWith("language-")
        );
        // Get the registered language name from Highlight.js
        let languageKey = languageClass
          ? languageClass.replace("language-", "")
          : "Unknown";
        let languageName =
          languageKey && hljs.getLanguage(languageKey)
            ? hljs.getLanguage(languageKey).name
            : "Unknown";
        /*
        let languageName = languageClass ? languageClass.replace('language-', '') : 'Unknown';
        if (languageName === 'plaintext') { languageName = 'Plain Text'; }
        */
        // Create the language div
        let languageDiv = Object.assign(document.createElement("div"), {
          className: "hljs-language",
          innerText: languageName,
        });
        // Insert the language div before the highlighted element
        el.parentElement.insertBefore(languageDiv, el);
      },
    });
    hljs.highlightAll();

    // Add button to <code> elements
    document.querySelectorAll(".ck-content pre code").forEach((codeElement) => {
      const button = document.createElement("button");
      button.className = "copy-code-button";
      const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
      <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z"/>
      </svg>`;
      button.innerHTML = svgIcon;
      button.addEventListener("click", () => {
        navigator.clipboard.writeText(codeElement.textContent);
        button.innerHTML = "<span>Copied!</span>";
        setTimeout(() => {
          button.innerHTML = svgIcon;
        }, 2000);
      });
      codeElement.appendChild(button);
    });
  }, []);

  const createdOn = new Date(created_on);
  const createdOnDateString = createdOn.toLocaleDateString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  return (
    <>
      <SiteHead title={meta_title} description={meta_description} />
      <Helmet>
        <link rel="preload" as="image" href={getBlogImagePath(image_name)} />
      </Helmet>
      <section className="single-blog-section">
        <Container>
          <div className="single-blog-container">
            <div className="img-container">
              <img
                className="blog-bg img-fluid"
                src={getBlogImagePath(image_name)}
                alt={alt_tag}
              />
            </div>
            <div className="text-content">
              <h4 className="title">{title}</h4>
              <div className="author-info">
                <span className="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10.357"
                    height="10.346"
                    viewBox="0 0 10.357 10.346"
                  >
                    <g transform="translate(0 -0.247)">
                      <path
                        d="M6.4,82.473l-5.7,5.7a.227.227,0,0,0-.059.1L.006,90.811a.224.224,0,0,0,.218.279.223.223,0,0,0,.054-.007l2.535-.632a.224.224,0,0,0,.1-.059l5.7-5.7Zm0,0"
                        transform="translate(0 -80.497)"
                      />
                      <path
                        d="M337.326,1.2l-.634-.634a1.149,1.149,0,0,0-1.586,0l-.777.777,2.221,2.221.777-.777a1.122,1.122,0,0,0,0-1.586Zm0,0"
                        transform="translate(-327.297 0)"
                      />
                    </g>
                  </svg>
                </span>
                <p className="author-name mb-0">by {author}</p>
                <p className="date mb-0">{createdOnDateString}</p>
              </div>
              <div
                className="inner-blog-content-dangerous ck-content"
                dangerouslySetInnerHTML={{ __html: content }}
              ></div>

              {/* <div className="text-block">
                <p className="text">
                  Last block of text.  This has a class names on it.
                </p>
              </div> */}
            </div>
          </div>
        </Container>
      </section>

      {/* <section className="related-blog-section">
        <Container>
          <h4 className="title">Related Blogs</h4>
          <div className="blogs-container">
            <BlogCard
              title={"What is an Earnings Call?"}
              coverImg={CardImg1}
              url={"what-is-an-earnings-call"}
              publishedDate={"09-20-2022"}
            />
            <BlogCard
              title={"What is an Earnings Call?"}
              coverImg={CardImg2}
              url={"what-is-an-earnings-call"}
              publishedDate={"09-20-2022"}
            />
            <BlogCard
              title={"What is an Earnings Call?"}
              coverImg={CardImg3}
              url={"what-is-an-earnings-call"}
              publishedDate={"09-20-2022"}
            />
          </div>
        </Container>
      </section> */}
    </>
  );
};
